import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, tap } from 'rxjs';
import { StatusComponent } from '../../../ui/status/status.component';
import { PaginationComponent } from '../../../inputs/pagination/pagination.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import {
  CampaignInsightsService,
  EXPORT_CAP,
  FailedSource,
} from '../../../../services/campaign/campaign-insights.service';
import { IMessageReportContacts } from '../../../../pages/organization/campaigns/types/messagesTypes';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';

/**
 * The numbers a campaign could not reach, across all of its sends, with the
 * reason each one failed and a CSV of the full list for retrying or cleaning.
 */
@Component({
  selector: 'app-campaign-failed-recipients',
  standalone: true,
  imports: [StatusComponent, PaginationComponent, HasPermissionDirective],
  templateUrl: './failed-recipients.component.html',
  styleUrl: './failed-recipients.component.css',
})
export class FailedRecipientsComponent implements OnChanges {
  private insights = inject(CampaignInsightsService);
  private destroyRef = inject(DestroyRef);
  private sub?: Subscription;

  @Input({ required: true }) campaignUuid = '';
  @Input() campaignName = '';
  /** The campaign's message uuids, for receipts that do not carry the campaign. */
  @Input() messageUuids: string[] = [];
  /** Message subjects by uuid, so each row can say which message failed. */
  @Input() subjects: Record<string, string> = {};
  /** Failures counted by the outcome above; shown until the list loads. */
  @Input() failedCount = 0;

  rows: IMessageReportContacts[] = [];
  state: 'loading' | 'ready' | 'error' = 'loading';
  page = 0;
  size = 10;
  total: number | null = null;

  exporting = false;
  exportError = '';
  /** The export note is a failure (announced as an alert). */
  exportFailed = false;

  /**
   * Where the first page found the failures (by campaign or by message
   * uuids); every later page and the export read the same list.
   */
  private source?: FailedSource;
  private uuidsKey = '';

  readonly formatCount = formatCount;
  readonly formatWhen = formatWhen;

  get totalPages(): number {
    const total = this.total ?? this.failedCount;
    return Math.max(1, Math.ceil(total / this.size));
  }

  get shownTotal(): number {
    return this.total ?? this.failedCount;
  }

  ngOnChanges(changes: SimpleChanges) {
    // The uuids array is rebuilt on every campaign refresh; only a new set counts.
    const uuidsKey = this.messageUuids.join(',');
    const uuidsChanged = uuidsKey !== this.uuidsKey;
    this.uuidsKey = uuidsKey;
    if (changes['campaignUuid'] || changes['failedCount'] || uuidsChanged) {
      this.page = 0;
      this.source = undefined;
      this.load();
    }
  }

  changePage(page: number) {
    this.page = Math.max(0, Number(page) || 0);
    this.load();
  }

  /**
   * Stores the new page size only: the pagination follows a size change with
   * changePage(0), and that loads the first page at the new size.
   */
  changeSize(size: number) {
    this.size = Number(size) || 10;
    this.page = 0;
  }

  retry() {
    this.load();
  }

  subjectOf(row: IMessageReportContacts): string {
    return this.subjects[row.messageUuid] || '';
  }

  private load() {
    this.sub?.unsubscribe();
    if (!this.campaignUuid) return;
    this.state = 'loading';
    // The first page picks the source; later pages keep to it.
    const source = this.page === 0 ? undefined : this.source;
    this.sub = this.insights
      .failedReceipts(this.campaignUuid, this.messageUuids, this.page, this.size, source)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.rows = result.rows;
          this.total = result.total;
          this.source = result.source;
          this.state = 'ready';
        },
        error: () => {
          this.state = 'error';
        },
      });
  }

  download() {
    if (this.exporting || !this.campaignUuid) return;
    this.exporting = true;
    this.exportError = '';
    this.exportFailed = false;

    // Read every page from the same source; the first page finds it when the
    // table has not yet.
    let source = this.source;
    this.insights
      .collectAll((page, size) =>
        this.insights
          .failedReceipts(this.campaignUuid, this.messageUuids, page, size, source)
          .pipe(tap((result) => (source = result.source)))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.exporting = false;
          if (!rows.length) {
            this.exportError = 'There are no failed numbers to download yet.';
            return;
          }
          const csv = toCsv(rows, [
            { label: 'Mobile number', value: (row) => row.mobileNumber },
            { label: 'Name', value: (row) => row.fullName },
            { label: 'Message', value: (row) => this.subjectOf(row) },
            { label: 'Group', value: (row) => row.group },
            { label: 'Delivery status', value: (row) => row.deliveryStatus },
            { label: 'Time', value: (row) => row.timeStamp },
          ]);
          downloadCsv(csvFileName('failed numbers', this.campaignName), csv);
          if (rows.length >= EXPORT_CAP) {
            this.exportError = `The file holds the first ${formatCount(EXPORT_CAP)} failed numbers.`;
          }
        },
        error: () => {
          this.exporting = false;
          this.exportFailed = true;
          this.exportError = 'The file could not be prepared. Check your connection and try again.';
        },
      });
  }
}
